import { useTheme } from '@emotion/react';
import { dehydrate, QueryClient, useQuery } from '@tanstack/react-query';
import {
    Anchor,
    Body,
    Box,
    BrowseStepper,
    ExperienceCard,
    ExperienceModal,
    Flex,
    Heading,
    Hero,
    HtmlContent,
    IconPlay,
    IconPlayModal,
    LightTable,
    LinkCard,
    ListDivider,
    MediaModal,
    MediaSlider,
    Meta,
    SpecificationListItem,
    Stack,
    Tag,
    Tile,
    useDisclosure,
    VStack,
    Wrapper,
} from 'designsystem';
import { GetStaticPaths, GetStaticProps } from 'next';
import { useRouter } from 'next/router';
import React, { FC, Ref, useEffect, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import {
    fetchApiData,
    getCredits,
    getMetaForFilmOrProjectDetails,
    gtm,
    LoadingFilmPage,
    slugifyTitle,
    useApi,
    useFilmInfo,
} from 'shared';
import { NumberParam, useQueryParam, useQueryParams } from 'use-query-params';
import HeroVideo from '../../../components/HeroVideo';
import { DocLabFilterQueryParams } from '../../../components/pages/DocLabCollectionPage';
import {
    DocLabFilmDocument,
    DocLabFilmQuery,
    DocLabFilmQueryVariables,
    DocLabFilterEnum,
    useDocLabFilmQuery,
    useDocLabFilmStepperQuery,
} from '../../../gql/api';
import loadIntlMessages from '../../../utils/loadIntlMessages';
import useBrowseStepper, { getStepperQueryVars, StepperData } from '../../../hooks/useBrowseStepper';
import PageSection from '../../../components/PageSection';
import { NextSeo } from 'next-seo';
import striptags from 'striptags';
import { DEFAULT_REVALIDATION_TIME } from '../../[...uri]';

const DoclabDetail: FC = () => {
    const { tokens } = useTheme();
    const { query, asPath, isFallback } = useRouter();
    const vars = {
        id: query.id as string,
    };
    const { data, isLoading } = useQuery(
        [...useDocLabFilmQuery.getKey(vars), { loggedIn: false }],
        useApi<DocLabFilmQuery, DocLabFilmQueryVariables>(DocLabFilmDocument).bind(null, vars)
    );
    const film = data?.film;

    const [filterParams] = useQueryParams(DocLabFilterQueryParams);
    const [offset] = useQueryParam('offset', NumberParam);
    const { data: stepperData, isLoading: isStepperLoading } = useDocLabFilmStepperQuery(
        getStepperQueryVars<DocLabFilterEnum>(filterParams, offset, query.query as string),
        { enabled: offset >= 0 }
    );

    const { nextFilm, current, total, onNext, onPrev } = useBrowseStepper(
        stepperData?.searchDocLab as StepperData,
        DocLabFilterQueryParams
    );

    const firstExpCardRef: Ref<HTMLDivElement> = useRef();

    const experienceModalProps = useDisclosure();
    const { onOpen: onOpenExperience } = experienceModalProps;
    const browseStepper = (
        <BrowseStepper
            title={<FormattedMessage defaultMessage="Explore the collection" />}
            body={
                <FormattedMessage
                    defaultMessage="{current, number}/{total, number} {total, plural, one {result} other {results}}"
                    values={{ current, total }}
                />
            }
            isLoading={isStepperLoading}
            current={offset + 1}
            total={total}
            prevDisabled={offset === 0}
            nextDisabled={!nextFilm}
            onPrev={onPrev}
            onNext={onNext}
        />
    );
    const [activeExperienceLink, setActiveExperienceLink] = useState('');
    const { locale } = useRouter();

    useEffect(() => {
        if (film) {
            gtm.pageView(asPath, {
                page_pillar: 'professionals',
                page_language: locale,
                film_id: film.id,
                film_name: film.fullPreferredTitle,
                film_edition: film.edition.year.toString(),
                film_section: film.sections?.join(', ') ?? '',
                film_subject:
                    film.subject?.map(s => s.translation).join(', ') ??
                    film.tags?.map(tag => tag.translation).join(', ') ??
                    '',
            });
        }
    }, [film, asPath, locale]);

    const {
        title,
        description,
        heroImage,
        ogImage,
        edition,
        mostRecentEdition,
        mediaImages,
        mediaVideos,
        mediaModalProps,
    } = useFilmInfo({
        film,
        filmOrProjectType: 'docLab',
    });
    if (isFallback || isLoading) {
        return <LoadingFilmPage />;
    }

    return (
        film && (
            <>
                <NextSeo
                    title={`${title} (${edition.year})`}
                    description={striptags(description)}
                    openGraph={{
                        images: ogImage,
                    }}
                    // the most recent film is the 'canonical' film to prevent a film from getting duplicated in search results.
                    canonical={
                        mostRecentEdition &&
                        `${process.env.NEXT_PUBLIC_BASE_URL ?? ''}/doclab/${mostRecentEdition.id}/${slugifyTitle(
                            film.fullPreferredTitle
                        )}`
                    }
                    titleTemplate="%s | IDFA DocLab"
                />
                <Hero
                    image={heroImage}
                    variant="dark"
                    w="100%"
                    rightChildren={
                        <>
                            {film.publications.docLabVideo && (
                                <HeroVideo videoUrl={film.publications.docLabVideo.value} />
                            )}
                            {film.publications.experiences?.length > 0 && (
                                <Tile colorScheme="neutral" gridRow="2">
                                    <Stack direction="column" spacing={4}>
                                        <Heading variant={2}>Experience this DocLab Project</Heading>
                                        <Body>Experience {film.fullPreferredTitle}</Body>
                                        <Flex
                                            as="button"
                                            alignItems="center"
                                            cursor="pointer"
                                            pt={2}
                                            onClick={() => {
                                                if (film.publications.experiences.length > 1) {
                                                    firstExpCardRef.current.scrollIntoView();
                                                }
                                                setActiveExperienceLink(film.publications.experiences[0].value);
                                                onOpenExperience();
                                            }}
                                        >
                                            <Box
                                                w={9}
                                                h={9}
                                                p={3}
                                                mr={4}
                                                backgroundColor={tokens.ColorNeutralWhite}
                                                color={tokens.SyntaxTextColorDefault}
                                                borderRadius="lg"
                                            >
                                                <IconPlayModal />
                                            </Box>
                                            <Body fontWeight={600}>{film.publications.experiences[0].title}</Body>
                                        </Flex>
                                    </Stack>
                                </Tile>
                            )}
                        </>
                    }
                >
                    <Flex flexDirection="column">
                        <Box mb={[4, null, 6]}>
                            <Tag size="l">
                                <Body fontSize={[4, null, 5]}>DocLab</Body>
                            </Tag>
                        </Box>
                        <Heading variant={1} clamp={2} mb={[2, null, 4]}>
                            {film.fullPreferredTitle}
                        </Heading>
                        <Box>
                            <Meta
                                {...getMetaForFilmOrProjectDetails({
                                    filmOrProject: film,
                                    filmOrProjectType: 'docLab',
                                })}
                                size="m"
                            />
                        </Box>
                    </Flex>
                </Hero>
                <Flex
                    flexDir="column"
                    bg={tokens.SyntaxBackgroundNeutralDarkest}
                    color={tokens.SyntaxTextColorOnDark}
                    pb={9}
                    position="relative"
                    minH="104px"
                >
                    {film.newMediaType && (
                        <Box as="section">
                            <Wrapper>
                                <ListDivider opacity={0.2} />
                                {film?.newMediaType && (
                                    <Box pt={[6, null, 8]}>
                                        <SpecificationListItem
                                            title="Type project"
                                            value={film.newMediaType.map(mediaType => mediaType.translation).join(', ')}
                                        />
                                    </Box>
                                )}
                            </Wrapper>
                        </Box>
                    )}
                    <Box
                        minH="152px"
                        display={['none', null, 'flex']}
                        position="absolute"
                        right={0}
                        px="84px"
                        bg={tokens.ColorNeutral100}
                    >
                        {browseStepper}
                    </Box>
                </Flex>

                <VStack direction="column" py={[9, null, 11]} spacing={[9, null, 11]}>
                    {film.description?.translation && (
                        <PageSection>
                            <Heading variant={3}>Description</Heading>
                            <Body>
                                <Stack spacing={4}>
                                    <HtmlContent html={film.description.translation} />
                                </Stack>
                            </Body>
                        </PageSection>
                    )}
                    <Box
                        display={['flex', null, 'none']}
                        bg={tokens.ColorNeutral10}
                        h="100%"
                        w="100%"
                        justifyContent="center"
                        py={9}
                    >
                        {browseStepper}
                    </Box>
                    {film.publications.experiences?.length > 0 && (
                        <PageSection>
                            <Heading variant={3}>Experiences</Heading>
                            {film.publications.experiences.map(experience => (
                                <Box key={experience.id} cursor="pointer" ref={firstExpCardRef}>
                                    <ExperienceCard
                                        onClick={() => {
                                            setActiveExperienceLink(experience.value);
                                            onOpenExperience();
                                        }}
                                        icon={<IconPlay />}
                                    >
                                        {experience.title}
                                    </ExperienceCard>
                                </Box>
                            ))}
                            {film.publications.links?.length > 0 && (
                                <VStack spacing={2}>
                                    {film.publications.links.map(link => (
                                        <Anchor key={link.value} href={link.value} w="100%">
                                            <LinkCard>{link.title}</LinkCard>
                                        </Anchor>
                                    ))}
                                </VStack>
                            )}
                        </PageSection>
                    )}
                    {mediaVideos.length > 0 && (
                        <Stack as="section" spacing={6} w="100%">
                            <Wrapper>
                                <Heading variant={3} as="h2">
                                    <FormattedMessage defaultMessage="Video's" />
                                </Heading>
                            </Wrapper>
                            <MediaSlider media={mediaVideos} />
                        </Stack>
                    )}
                    {mediaImages.length > 0 && (
                        <Stack as="section" spacing={6} w="100%">
                            <Wrapper>
                                <Heading variant={3} as="h2">
                                    <FormattedMessage defaultMessage="Images" />
                                </Heading>
                            </Wrapper>
                            <MediaSlider media={mediaImages} />
                        </Stack>
                    )}
                    <MediaModal
                        {...mediaModalProps}
                        closeLabel={<FormattedMessage defaultMessage="Close" description="Close modal" />}
                    />
                    {film.credits?.length > 0 && (
                        <PageSection>
                            <Heading variant={3}>Credits</Heading>
                            <LightTable lines={getCredits(film.credits)} />
                        </PageSection>
                    )}
                </VStack>
                <ExperienceModal
                    url={activeExperienceLink}
                    closeLabel={<FormattedMessage defaultMessage="Close" description="Close modal" />}
                    {...experienceModalProps}
                />
            </>
        )
    );
};

export const getStaticPaths: GetStaticPaths = async () => ({
    // we don't provide paths because we won't fetch any films during build time.
    paths: [],

    fallback: true,
});

export const getStaticProps: GetStaticProps = async ({ params, locale }) => {
    const queryClient = new QueryClient();

    const variables = {
        id: params?.id as string,
    };
    const data = await queryClient.fetchQuery([...useDocLabFilmQuery.getKey(variables), { loggedIn: false }], () =>
        fetchApiData<DocLabFilmQuery, DocLabFilmQueryVariables>({
            locale,
            query: useDocLabFilmQuery.document,
            variables,
        })
    );
    if (!data.film) {
        return {
            notFound: true,
            revalidate: DEFAULT_REVALIDATION_TIME,
        };
    }

    return {
        props: {
            intlMessages: await loadIntlMessages(locale),
            dehydratedState: dehydrate(queryClient),
        },
        revalidate: DEFAULT_REVALIDATION_TIME,
    };
};

export default DoclabDetail;
